import type React from 'react';
import { useSurfaces } from '@/hooks/useSurfaces';
import { NoticeRedirect } from './RouteNotice';

// Generic, backend-driven route guard (SENSARRA-VERTICAL-TENANT-SURFACE-1).
// Admits a route only if its `surface` id is in the caller's authoritative
// `visible_surfaces` (GET /api/v1/me/surfaces). Fail-closed:
//   * If the surface is already in the caller's set (customer-safe default, or the
//     Director fast path, or an operator whose snapshot resolved) → render.
//   * If not yet resolved → show a brief "checking" state, NEVER the child. So an
//     operator hitting an operator route is not wrongly bounced, and a customer is
//     never shown the surface for even one frame.
//   * Once resolved and still not permitted → redirect to the caller's role home
//     (a surface they CAN see), not a hardcoded /dashboard.
//
// This is the UX gate; the backend re-enforces every action server-side.
export function RequireSurface({
  surface,
  children,
}: {
  surface: string;
  children: React.ReactNode;
}) {
  const { has, settled, homePath } = useSurfaces();

  if (has(surface)) {
    return <>{children}</>;
  }

  if (!settled) {
    return (
      <div className="flex h-full min-h-[40vh] items-center justify-center text-sm text-gray-400">
        Checking access…
      </div>
    );
  }

  return <NoticeRedirect to={homePath} message="That area is not available on your account." />;
}
